/**
 * iter62 — Shared schedule live-games hook.
 *
 * Fetches a tournament's schedule and exposes:
 *   - `liveEntries`: rows with status === "live" (most recently kicked off first)
 *   - `liveCount`: liveEntries.length
 *   - `tickerEntries` (iter87.359): live rows PLUS anything within a
 *     kickoff ±4.5h/+8h window — upcoming later today/tomorrow and
 *     recently-finished games — same window NFL's `/live-ticker`
 *     endpoint uses, so every sport's dashboard ticker can show more
 *     than just mid-game snapshots.
 *   - `hasLiveOrToday`: ≥1 entry is live OR scheduled today (UTC)
 *
 * Auto-refresh: polls every 75s when `hasLiveOrToday` is true. Pauses on
 * hidden tabs. Returns `{ liveEntries, liveCount, tickerEntries, hasLiveOrToday, refresh }`.
 *
 * Used by both the LeagueDashboard (badge + inline strip) and ScheduleWidget
 * (cell rendering) so they share a single source of truth and avoid
 * duplicate /schedule fetches.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const POLL_MS = 75_000;
// iter87.359 (user-requested — "I like the logic the NFL ticker is
// using... could that be applied to MLB and club soccer (all the
// games)?") — same trailing/leading window NFL's dedicated `/live-
// ticker` endpoint already uses (kickoff −4.5h … +8h), just computed
// client-side from the schedule already fetched here instead of a
// second backend call. Zero extra vendor/API cost.
const TICKER_WINDOW_BEFORE_MS = 4.5 * 60 * 60 * 1000;
const TICKER_WINDOW_AFTER_MS = 8 * 60 * 60 * 1000;

export function useScheduleLive(tournamentId) {
    const [entries, setEntries] = useState([]);

    const refresh = useCallback(async () => {
        if (!tournamentId) return;
        try {
            const d = await api.getSchedule(tournamentId);
            setEntries(Array.isArray(d?.entries) ? d.entries : []);
        } catch {
            // Silent — schedule is non-critical UI surface. Polling will retry.
        }
    }, [tournamentId]);

    useEffect(() => { refresh(); }, [refresh]);

    const liveEntries = useMemo(
        () => entries.filter(e => e.status === "live"),
        [entries],
    );

    // iter87.359 — "Games On Now" ticker feed: live games (always, no
    // matter how long ago they kicked off) PLUS anything else whose
    // kickoff falls in the ±window — upcoming later today (or spilling
    // into tomorrow), and games that wrapped up recently. This is what
    // made the NFL ticker feel "more robust" than soccer's live-only
    // strip, which only ever showed something mid-game.
    const tickerEntries = useMemo(() => {
        const now = Date.now();
        return entries
            .filter((e) => {
                if (e.status === "live") return true;
                if (!e.scheduled_at) return false;
                const t = new Date(e.scheduled_at).getTime();
                if (Number.isNaN(t)) return false;
                return t >= now - TICKER_WINDOW_BEFORE_MS && t <= now + TICKER_WINDOW_AFTER_MS;
            })
            .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""));
    }, [entries]);

    const hasLiveOrToday = useMemo(() => {
        if (entries.length === 0) return false;
        if (liveEntries.length > 0) return true;
        const todayIso = new Date().toISOString().slice(0, 10);
        return entries.some(e => {
            if (e.played || !e.scheduled_at) return false;
            return e.scheduled_at.slice(0, 10) === todayIso;
        });
    }, [entries, liveEntries]);

    useEffect(() => {
        if (!hasLiveOrToday) return undefined;
        const id = setInterval(() => {
            if (typeof document !== "undefined" && document.hidden) return;
            refresh();
        }, POLL_MS);
        return () => clearInterval(id);
    }, [hasLiveOrToday, refresh]);

    return { liveEntries, liveCount: liveEntries.length, tickerEntries, hasLiveOrToday, refresh };
}
